import { Component } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { LayoutService } from '@layout/service/app.layout.service';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CaseConfigService } from '@shared/services/case-config.service';
import { CustomerCaseContextService } from '@shared/services/customer-case-context.service';

const HIDDEN_BREADCRUMBS = new Set(['Configuración', 'Documentación KYC Paycomet']);

interface Breadcrumb {
  label: string;
  url?: string;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './app.breadcrumb.component.html',
})
export class AppBreadcrumbComponent {
  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  extra?: { coincidence: string; extra: string };
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();
  groupCriteria: string | null = null;
  groupCriteriaLabel: string | null = null;
  url: string = '';
  hidden = false;
  portfolios: any[] | null = [];

  constructor(
    public router: Router,
    public layout: LayoutService,
    private caseConfigService: CaseConfigService,
    private contextService: CustomerCaseContextService,
  ) {
    this.contextService.portfolios$.subscribe((portfolios) => {
      this.portfolios = portfolios;
      this.setGroupCriteria();
    });

    this.layout.getCustomBreadcrumb().subscribe((extra: any) => {
      this.extra = extra;
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((_) => {
      const root = this.router.routerState.snapshot.root;
      const breadcrumbs: Breadcrumb[] = [];
      this.addBreadcrumb(root, [], breadcrumbs);
      this.hidden = this.routeHasFlag(root, 'hideBreadcrumb');
      this.url = breadcrumbs[0]?.url ?? '';
      this._breadcrumbs$.next(breadcrumbs);

      this.setGroupCriteria();
    });
  }

  private setGroupCriteria() {
    const group = this.caseConfigService.getCaseConfig();

    if (!group) return;

    this.groupCriteria = group!.groupCriteria;
    const text =
      'Los expedientes agrupan las operaciones de un mismo cliente. Esta organización se aplica tanto en notificaciones como en la página de pago. El criterio de agrupación actual es por';

    switch (group!.groupCriteria) {
      case 'portfolio':
        const aliases = this.portfolios?.map((p) => p.alias).filter(Boolean) ?? [];
        const firstThree = aliases.slice(0, 3);
        const aliasList = firstThree.length ? ` (ej: ${firstThree.join(', ')})` : '';
        this.groupCriteriaLabel = text + ' Cartera' + aliasList;
        break;
      case 'single_operation':
        this.groupCriteriaLabel = text + ' Operación';
        break;
      case 'custom_field':
        const labels = group.configParams?.length
          ? group.configParams.join(', ')
          : 'campo personalizado';
        this.groupCriteriaLabel = text + labels;
        break;
      default:
        this.groupCriteriaLabel = null;
    }
  }

  private routeHasFlag(route: ActivatedRouteSnapshot, flag: string): boolean {
    if (route.data[flag]) return true;
    return route.firstChild ? this.routeHasFlag(route.firstChild, flag) : false;
  }

  private addBreadcrumb(
    route: ActivatedRouteSnapshot,
    parentUrl: string[],
    breadcrumbs: Breadcrumb[],
  ) {
    const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
    const breadcrumb = route.data['breadcrumb'];
    const parentBreadcrumb =
      route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;

    if (!HIDDEN_BREADCRUMBS.has(breadcrumb) && breadcrumb && breadcrumb !== parentBreadcrumb) {
      breadcrumbs.push({
        label: route.data['breadcrumb'],
        url: '/' + routeUrl.join('/'),
      });
    }

    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
  }

  getCount() {
    return this._breadcrumbs$.value.length;
  }
}
